import { FunctionComponent, KeyboardEvent, useRef, useState } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

interface EmailTagInputProps {
	emails: string[];
	onChange: (emails: string[]) => void;
	placeholder?: string;
	name: string;
}

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const EmailTagInput: FunctionComponent<EmailTagInputProps> = ({
	emails,
	onChange,
	placeholder = "e.g. colleague@company.com",
	name,
}) => {
	const [inputValue, setInputValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const addEmail = (raw: string) => {
		const trimmed = raw.trim();

		if (!trimmed || !isValidEmail(trimmed)) {
			return;
		}

		if (emails.includes(trimmed)) {
			return;
		}

		onChange([...emails, trimmed]);
		setInputValue("");
	};

	const removeEmail = (index: number) => {
		onChange(emails.filter((_, i) => i !== index));
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
			if (inputValue.trim()) {
				e.preventDefault();
				addEmail(inputValue);
			}
		} else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
			removeEmail(emails.length - 1);
		}
	};

	const handleBlur = () => {
		if (inputValue.trim()) {
			addEmail(inputValue);
		}
	};

	return (
		<div
			className={classNames(
				"flex flex-wrap items-center gap-1 w-full min-h-[28px] px-2 py-1",
				"bg-white border border-border-light rounded-md",
				"focus-within:border-brand-light cursor-text"
			)}
			onClick={() => inputRef.current?.focus()}
		>
			{emails.map((email, index) => (
				<span
					key={`${name}-${email}`}
					className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-brand-white border border-border-light rounded-full text-text"
				>
					{email}
					<button
						className="flex items-center justify-center hover:text-status-error cursor-pointer"
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							removeEmail(index);
						}}
					>
						<Icon className="w-2.5 h-2.5" icon={faXmark} />
					</button>
				</span>
			))}
			<input
				ref={inputRef}
				className="flex-1 min-w-[120px] text-sm outline-none bg-transparent placeholder-placeholder"
				name={name}
				placeholder={emails.length === 0 ? placeholder : ""}
				type="text"
				value={inputValue}
				onBlur={handleBlur}
				onChange={(e) => setInputValue(e.target.value)}
				onKeyDown={handleKeyDown}
			/>
		</div>
	);
};
